import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../config/axios';
import { formatError } from '../utils/errorHandler';
import FortnoxReferencePicker from './FortnoxReferencePicker';
import './TaskFortnoxBillableSection.css';

const TaskFortnoxBillableSection = ({ taskId, disabled = false }) => {
  const { t } = useTranslation(['tasks', 'common']);
  const [loading, setLoading] = useState(true);
  const [fortnoxStatus, setFortnoxStatus] = useState(null);
  const [lines, setLines] = useState([]);
  const [sectionError, setSectionError] = useState('');
  const [planLimited, setPlanLimited] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newArticleNumber, setNewArticleNumber] = useState(null);
  const [newQuantity, setNewQuantity] = useState('1');
  const [newDescription, setNewDescription] = useState('');
  const [removingId, setRemovingId] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setSectionError('');
      setPlanLimited(false);
      setFortnoxStatus(null);
      setLines([]);
      try {
        const statusRes = await axios.get('/api/integrations/fortnox/status');
        if (cancelled) {
          return;
        }
        const status = statusRes.data?.data || null;
        setFortnoxStatus(status);
        const ready = Boolean(status?.connected && !status?.needsReconnect);
        if (!ready) {
          return;
        }

        const linesRes = await axios.get(`/api/tasks/${taskId}/billable-articles`);
        if (cancelled) {
          return;
        }
        setLines(linesRes.data?.data?.lines || []);
      } catch (err) {
        if (!cancelled) {
          if (err.response?.status === 403 && err.response?.data?.code === 'PLAN_LIMIT') {
            setPlanLimited(true);
          } else {
            setSectionError(formatError(err) || t('fortnoxBillableLoadFailed'));
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    if (taskId) {
      load();
    }

    return () => {
      cancelled = true;
    };
  }, [taskId, t]);

  const fortnoxReady = Boolean(fortnoxStatus?.connected && !fortnoxStatus?.needsReconnect);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newArticleNumber) {
      setSectionError(t('fortnoxBillableArticleRequired'));
      return;
    }
    setAdding(true);
    setSectionError('');
    try {
      const response = await axios.post(`/api/tasks/${taskId}/billable-articles`, {
        articleNumber: newArticleNumber,
        quantity: Number(newQuantity) || 1,
        descriptionOverride: newDescription.trim() || null
      });
      setLines(response.data?.data?.lines || []);
      setNewArticleNumber(null);
      setNewQuantity('1');
      setNewDescription('');
    } catch (err) {
      setSectionError(formatError(err) || t('fortnoxBillableAddFailed'));
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (lineId) => {
    setRemovingId(lineId);
    setSectionError('');
    try {
      const response = await axios.delete(`/api/tasks/${taskId}/billable-articles/${lineId}`);
      setLines(response.data?.data?.lines || []);
    } catch (err) {
      setSectionError(formatError(err) || t('fortnoxBillableRemoveFailed'));
    } finally {
      setRemovingId(null);
    }
  };

  if (loading) {
    return null;
  }

  if (planLimited) {
    return null;
  }

  // Hide only when Fortnox status loaded and billing is not active
  if (fortnoxStatus && !fortnoxReady) {
    return null;
  }

  // Status/lines load failed — surface error instead of pretending inactive
  if (!fortnoxReady) {
    if (!sectionError) {
      return null;
    }
    return (
      <div className="task-fortnox-billable">
        <div className="error">{sectionError}</div>
      </div>
    );
  }

  return (
    <div className="task-fortnox-billable">
      <h3 className="task-section-label">{t('fortnoxBillableTitle')}</h3>
      <p className="section-description">{t('fortnoxBillableDescription')}</p>

      {sectionError && <div className="error">{sectionError}</div>}

      {lines.length > 0 && (
        <ul className="fortnox-billable-lines">
          {lines.map((line) => (
            <li key={line.id} className="fortnox-billable-line">
              <div className="fortnox-billable-line-info">
                <span className="fortnox-billable-article">{line.articleNumber}</span>
                <span className="fortnox-billable-qty">
                  {t('fortnoxBillableQty', { quantity: line.quantity })}
                </span>
                {line.descriptionOverride && (
                  <span className="fortnox-billable-desc">{line.descriptionOverride}</span>
                )}
              </div>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => handleRemove(line.id)}
                disabled={disabled || removingId === line.id}
              >
                {t('common:delete')}
              </button>
            </li>
          ))}
        </ul>
      )}

      {lines.length === 0 && (
        <p className="fortnox-billable-empty">{t('fortnoxBillableEmpty')}</p>
      )}

      <form className="fortnox-billable-add-form" onSubmit={handleAdd}>
        <div className="fortnox-billable-add-row">
          <FortnoxReferencePicker
            type="article"
            value={newArticleNumber}
            onChange={setNewArticleNumber}
            disabled={disabled || adding}
            placeholder={t('fortnoxBillableArticlePlaceholder')}
          />
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={newQuantity}
            onChange={(e) => setNewQuantity(e.target.value)}
            disabled={disabled || adding}
            aria-label={t('fortnoxBillableQuantityLabel')}
          />
          <input
            type="text"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            placeholder={t('fortnoxBillableDescriptionPlaceholder')}
            disabled={disabled || adding}
            aria-label={t('fortnoxBillableDescriptionPlaceholder')}
          />
          <button
            type="submit"
            className="btn btn-primary btn-sm"
            disabled={disabled || adding}
          >
            {t('fortnoxBillableAddLine')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TaskFortnoxBillableSection;
